"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateReportPDF = exports.previewReportHTML = exports.generatePDFFromHTML = exports.previewHTMLInNewWindow = exports.renderTemplateWithData = exports.fetchTemplateWithData = exports.setGradeReportConfig = void 0;
const mustache_1 = __importDefault(require("mustache"));
const config_1 = require("./config");
var config_2 = require("./config");
Object.defineProperty(exports, "setGradeReportConfig", { enumerable: true, get: function () { return config_2.setConfig; } });
/**
 * Get authentication headers for API requests
 * Checks both 'currentUser' (iteach) and 'user' (iLearn) localStorage keys
 */
const getAuthHeaders = () => {
    let token = '';
    // Try iteach key first, then iLearn key
    const raw = localStorage.getItem('currentUser') || localStorage.getItem('user');
    if (raw && raw !== 'undefined') {
        try {
            const parsed = JSON.parse(raw);
            token = parsed.token || '';
        }
        catch (error) {
            console.error('Failed to parse auth from localStorage:', error);
        }
    }
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
};
/**
 * Fetch test tag mappings from backend (for School Report Card categorization)
 */
const fetchTestTagMappings = async () => {
    try {
        const url = `${(0, config_1.getConfig)().nodeSiteUrl}v1/reports/test-tag-mappings`;
        console.log('[fetchTestTagMappings] Fetching test tag mappings');
        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
        });
        if (!response.ok) {
            console.error('[fetchTestTagMappings] Failed to fetch:', response.statusText);
            return null;
        }
        const result = await response.json();
        if (!result.success || !result.data) {
            console.error('[fetchTestTagMappings] Invalid response');
            return null;
        }
        console.log('[fetchTestTagMappings] Fetched mappings:', result.summary);
        return result.data;
    }
    catch (error) {
        console.error('[fetchTestTagMappings] Error:', error);
        return null;
    }
};
/**
 * Fetch template HTML and student data from backend
 */
const fetchTemplateWithData = async (templateId, students, filters, debugMode) => {
    try {
        const url = `${(0, config_1.getConfig)().nodeSiteUrl}v1/reports/get-template-with-data`;
        console.log('[fetchTemplateWithData] Fetching template:', templateId);
        // Fetch tag mappings for School Report Card (templateId 4)
        let tagMappings = null;
        if (templateId === 4) {
            console.log('[fetchTemplateWithData] Fetching tag mappings for School Report Card');
            tagMappings = await fetchTestTagMappings();
            if (tagMappings) {
                console.log('[fetchTemplateWithData] Tag mappings fetched successfully');
                console.log('[fetchTemplateWithData] Number of tests with tags:', Object.keys(tagMappings).length);
                console.log('[fetchTemplateWithData] Sample tag mapping:', Object.entries(tagMappings)[0]);
            }
            else {
                console.warn('[fetchTemplateWithData] No tag mappings found, backend will auto-fetch');
            }
        }
        // Build request payload
        const requestPayload = {
            templateId,
            students,
            ...(filters && { filters }),
            ...(debugMode && { debugMode: true })
        };
        // Add tagMappings if available
        if (tagMappings) {
            requestPayload.tagMappings = tagMappings;
            console.log('[fetchTemplateWithData] ✅ ADDING tagMappings to payload');
        }
        else {
            console.log('[fetchTemplateWithData] ❌ NO tagMappings to add');
        }
        console.log('[fetchTemplateWithData] Final payload keys:', Object.keys(requestPayload));
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(requestPayload)
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error('[fetchTemplateWithData] Error response:', errorText);
            throw new Error(`Failed to fetch template: ${response.statusText}`);
        }
        const result = await response.json();
        console.log('[fetchTemplateWithData] Response:', result);
        if (!result.success || !result.data) {
            throw new Error('Invalid response from server');
        }
        return {
            templateHtml: result.data.templateHtml,
            students: result.data.students,
            templateName: result.data.templateName,
            batchStatistics: result.data.batchStatistics,
            batchMetadata: result.data.batchMetadata,
            isBatchReport: result.data.metadata?.isBatchReport || false,
            analyticsData: result.data.analyticsData,
        };
    }
    catch (error) {
        console.error('[fetchTemplateWithData] Error:', error);
        return null;
    }
};
exports.fetchTemplateWithData = fetchTemplateWithData;
/**
 * Transform student data into flat structure expected by templates
 */
const transformStudentData = (student) => {
    // Check if student data is already enriched by backend
    // Look for: 'tests' array (PTM/Dossier), 'subjects' array (School/IB Report Card),
    // 'literacySkills' (Pre-Primary), or 'gradeBoundaries' (IB — always present even with empty subjects)
    const hasTests = student.tests && Array.isArray(student.tests) && student.tests.length > 0;
    const hasSubjects = student.subjects && Array.isArray(student.subjects);
    const hasLiteracySkills = student.literacySkills && Array.isArray(student.literacySkills) && student.literacySkills.length > 0;
    const hasGradeBoundaries = student.gradeBoundaries && Array.isArray(student.gradeBoundaries);
    const isEnriched = hasTests || hasSubjects || hasLiteracySkills || hasGradeBoundaries;
    if (isEnriched) {
        // Student is already enriched by backend - return as-is with minimal additions
        console.log('[transformStudentData] Student already enriched, using backend data. Has tests:', hasTests, 'Has subjects:', hasSubjects, 'Has literacySkills:', hasLiteracySkills);
        return {
            ...student,
            // Add any missing helper fields
            currentDate: student.currentDate || new Date().toLocaleDateString(),
            currentYear: student.currentYear || new Date().getFullYear(),
            // Keep original structure
            _raw: student
        };
    }
    // Legacy path: transform raw student data (for backwards compatibility)
    console.log('[transformStudentData] Transforming raw student data');
    const studentInfo = student.StudentInfo || {};
    const testInfo = student.TestInfo || [];
    const attendanceInfo = student.AttendanceInfo || [];
    // Calculate aggregate test scores
    const totalTestsScored = testInfo.reduce((sum, test) => sum + (test.totalScored || 0), 0);
    const totalTestsMaxMarks = testInfo.reduce((sum, test) => sum + (test.totalMaxMarks || 0), 0);
    const overallPercentage = totalTestsMaxMarks > 0 ? ((totalTestsScored / totalTestsMaxMarks) * 100).toFixed(2) : 0;
    // Calculate attendance
    const totalPresents = attendanceInfo.reduce((sum, att) => sum + (att.Presents || 0), 0);
    const totalAbsents = attendanceInfo.reduce((sum, att) => sum + (att.Absents || 0), 0);
    const totalClasses = totalPresents + totalAbsents;
    const attendancePercentage = totalClasses > 0 ? ((totalPresents / totalClasses) * 100).toFixed(2) : 0;
    // Flatten student info and add computed fields
    return {
        // Student basic info (flat)
        studentId: student.Id || 'N/A',
        studentName: studentInfo.FullName || 'N/A',
        firstName: studentInfo.FirstName || 'N/A',
        lastName: studentInfo.LastName || 'N/A',
        email: studentInfo.Email || 'N/A',
        mobile: studentInfo.Mobile || 'N/A',
        enrollmentId: student.EnrollmentId || 'N/A',
        // Class/Batch info
        className: studentInfo.BatchName || 'N/A',
        batchName: studentInfo.BatchName || 'N/A',
        batchCode: studentInfo.BatchCode || 'N/A',
        courseName: studentInfo.CourseName || 'N/A',
        courseCode: studentInfo.CourseCode || 'N/A',
        programId: studentInfo.ProgramId || 'N/A',
        programName: studentInfo.ProgramName || 'N/A',
        programCode: studentInfo.ProgramCode || 'N/A',
        schoolName: studentInfo.SchoolName || 'N/A',
        schoolCode: studentInfo.SchoolCode || 'N/A',
        yearName: studentInfo.YearName || 'N/A',
        // Academic performance
        overallPercentage,
        totalTestsScored,
        totalTestsMaxMarks,
        averageScore: student.averageScore || overallPercentage,
        rank: student.rank || 'N/A',
        // Attendance
        attendancePercentage,
        totalPresents,
        totalAbsents,
        totalClasses,
        // Test details (array)
        tests: testInfo.map((test) => ({
            testId: test.testId,
            testName: test.testName || 'N/A',
            testCategory: test.testCategoryName || 'N/A',
            scored: test.totalScored || 0,
            maxMarks: test.totalMaxMarks || 0,
            percentage: test.totalMaxMarks > 0 ? ((test.totalScored / test.totalMaxMarks) * 100).toFixed(2) : 0,
            sections: test.sections || []
        })),
        // Attendance details (array)
        attendance: attendanceInfo.map((att) => ({
            month: att.Month || 'N/A',
            year: att.Year || 'N/A',
            presents: att.Presents || 0,
            absents: att.Absents || 0,
            totalClasses: att.TotalClasses || 0,
            percentage: att.TotalClasses > 0 ? ((att.Presents / att.TotalClasses) * 100).toFixed(2) : 0,
            topicName: att.TopicName || 'N/A'
        })),
        // Date info
        currentDate: new Date().toLocaleDateString(),
        currentYear: new Date().getFullYear(),
        // Keep original structure for advanced templates
        _raw: student
    };
};
/**
 * Render HTML by applying student data to template using Mustache
 * @param templateHtml - Mustache template HTML
 * @param students - Array of student data
 * @param batchStatistics - Optional batch-level statistics (for batch reports)
 * @param batchMetadata - Optional batch metadata (for batch reports)
 * @param isBatchReport - Whether this is a batch report or individual student reports
 */
const renderTemplateWithData = (templateHtml, students, batchStatistics, batchMetadata, isBatchReport = false, analyticsData, debugMode) => {
    try {
        console.log('[renderTemplateWithData] Rendering template with', students.length, 'students');
        console.log('[renderTemplateWithData] Batch report mode:', isBatchReport);
        // For batch reports, render single template with all students
        if (isBatchReport && batchMetadata) {
            console.log('[renderTemplateWithData] Batch statistics:', batchStatistics);
            console.log('[renderTemplateWithData] Analytics data:', analyticsData);
            console.log('[renderTemplateWithData] Batch metadata:', batchMetadata);
            // Create template context with batch data and student array
            const templateContext = {
                // Batch-level data
                ...batchMetadata,
                ...batchStatistics,
                ...analyticsData,
                // Students array for iteration in template
                students: students,
                // Debug mode for info tooltips
                ...(debugMode && { debugMode: true }),
            };
            console.log('[renderTemplateWithData] Rendering batch template with context');
            const rendered = mustache_1.default.render(templateHtml, templateContext);
            return rendered;
        }
        // For individual student reports, render template for each student and concatenate
        const renderedStudents = students.map((student, index) => {
            // Transform student data into flat structure
            const transformedData = transformStudentData(student);
            if (transformedData.tests && transformedData.tests.length > 0) {
                console.log('[renderTemplateWithData] First test sections:', transformedData.tests[0].sections?.length || 0);
            }
            return mustache_1.default.render(templateHtml, { ...transformedData, ...(debugMode && { debugMode: true }) });
        });
        // Combine all rendered HTML with page breaks
        const combinedHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Grade Report</title>
        <style>
          /* Page break for print/PDF */
          .student-report {
            page-break-after: always;
            page-break-inside: avoid;
            margin-bottom: 20px;
          }
          .student-report:last-child {
            page-break-after: auto;
          }
          /* Common styles */
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          @media print {
            .student-report {
              page-break-after: always;
              page-break-inside: avoid;
            }
            .student-report:last-child {
              page-break-after: auto;
            }
          }
        </style>
      </head>
      <body>
        ${renderedStudents.map((html, index) => `
          <div class="student-report" data-student-index="${index}">
            ${html}
          </div>
        `).join('')}
      </body>
      </html>
    `;
        return combinedHtml;
    }
    catch (error) {
        console.error('[renderTemplateWithData] Error rendering template:', error);
        return '<html><body><h1>Error rendering template</h1><p>' + error.message + '</p></body></html>';
    }
};
exports.renderTemplateWithData = renderTemplateWithData;
/**
 * Preview HTML in a new window
 */
const previewHTMLInNewWindow = (html, title = 'Preview') => {
    const previewWindow = window.open('', '_blank');
    if (previewWindow) {
        previewWindow.document.write(html);
        previewWindow.document.title = title;
        previewWindow.document.close();
    }
    else {
        alert('Please allow popups to preview the report');
    }
};
exports.previewHTMLInNewWindow = previewHTMLInNewWindow;
/**
 * Generate PDF from HTML using server-side Puppeteer.
 * Sends the rendered HTML to nodefork's /reports/html-to-pdf endpoint,
 * which uses Puppeteer (headless Chrome) for pixel-perfect PDF rendering.
 */
const generatePDFFromHTML = async (html, filename = 'report.pdf', options) => {
    try {
        console.log('[generatePDFFromHTML] Generating PDF via server:', filename);
        const url = `${(0, config_1.getConfig)().nodeSiteUrl}v1/reports/html-to-pdf`;
        const response = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ html, filename, options }),
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error('[generatePDFFromHTML] Server error:', errorText);
            throw new Error(`PDF generation failed: ${response.statusText}`);
        }
        // Download the PDF blob
        const blob = await response.blob();
        const downloadUrl = URL.createObjectURL(blob);
        // Trigger browser download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        console.log('[generatePDFFromHTML] PDF downloaded successfully');
    }
    catch (error) {
        console.error('[generatePDFFromHTML] Error generating PDF:', error);
        throw error;
    }
};
exports.generatePDFFromHTML = generatePDFFromHTML;
/**
 * Full flow: Fetch template, render with data, and preview HTML
 */
const previewReportHTML = async (templateId, students, filters) => {
    try {
        const debugMode = localStorage.getItem('debugMode') === 'true'
            || sessionStorage.getItem('demoMode') === 'true';
        const templateData = await (0, exports.fetchTemplateWithData)(templateId, students, filters, debugMode);
        if (!templateData) {
            throw new Error('Failed to fetch template data');
        }
        const renderedHtml = (0, exports.renderTemplateWithData)(templateData.templateHtml, templateData.students, // Use enriched students from backend
        templateData.batchStatistics, templateData.batchMetadata, templateData.isBatchReport, templateData.analyticsData, debugMode);
        (0, exports.previewHTMLInNewWindow)(renderedHtml, `Preview: ${templateData.templateName}`);
    }
    catch (error) {
        console.error('[previewReportHTML] Error:', error);
        alert('Failed to preview report: ' + error.message);
    }
};
exports.previewReportHTML = previewReportHTML;
/**
 * Full flow: Fetch template, render with data, and generate PDF
 */
const generateReportPDF = async (templateId, students, filename, filters) => {
    try {
        const debugMode = localStorage.getItem('debugMode') === 'true'
            || sessionStorage.getItem('demoMode') === 'true';
        const templateData = await (0, exports.fetchTemplateWithData)(templateId, students, filters, debugMode);
        if (!templateData) {
            throw new Error('Failed to fetch template data');
        }
        const renderedHtml = (0, exports.renderTemplateWithData)(templateData.templateHtml, templateData.students, // Use enriched students from backend
        templateData.batchStatistics, templateData.batchMetadata, templateData.isBatchReport, templateData.analyticsData, debugMode);
        const pdfFilename = filename || `${templateData.templateName}-${new Date().toISOString().split('T')[0]}.pdf`;
        await (0, exports.generatePDFFromHTML)(renderedHtml, pdfFilename);
    }
    catch (error) {
        console.error('[generateReportPDF] Error:', error);
        alert('Failed to generate PDF: ' + error.message);
    }
};
exports.generateReportPDF = generateReportPDF;
